import { Box } from '@mui/material';
import React, { useEffect, useRef, useState, CSSProperties } from 'react';
import { makeStyles } from 'tss-react/mui';
import Icon from '@ui/Icon';
import NoResultsFoundV2 from '@replacements/components/ReplacementsDictionary/NoResultsFoundV2';
import NoWordEntered from '@replacements/components/ReplacementsDictionary/NoWordEntered';
import SmartSuggestionsLoading from '@replacements/components/ReplacementsDictionary/SmartSuggestionsLoading';
import DictionarySuggestionListItem from '@replacements/components/ReplacementsDictionary/DictionarySuggestionListItem';
import { ReplacementSuggestion } from '@replacements/types/replacementsDictionaryTypes';

interface SmartSuggestionsBodyProps {
  wordInput?: string;
  loading: boolean;
  suggestions: ReplacementSuggestion[];
  replacementInput?: string;
}

const useStyles = makeStyles()(theme => ({
  bodyRoot: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    overflowX: 'hidden',
    overflowY: 'auto',
    ...(theme.mixins.scrollBar as CSSProperties),
  },
  resultsContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
}));

const getBodyContents = (
  props: SmartSuggestionsBodyProps,
  classes: ReturnType<typeof useStyles>['classes']
) => {
  const { wordInput, loading, suggestions, replacementInput } = props;
  if (!wordInput) {
    return <NoWordEntered />;
  }
  if (loading) {
    return <SmartSuggestionsLoading />;
  }
  if (!suggestions || suggestions.length === 0) {
    return (
      <NoResultsFoundV2
        icon={<Icon type="faceSad" fontSize="large" />}
        subtitle={`We couldn’t generate a helpful phonetic spelling for this word. Try spelling it another way, or create your own.`}
      />
    );
  }
  return (
    <Box className={classes.resultsContainer}>
      {suggestions.map((suggestion, index) => (
        <DictionarySuggestionListItem
          key={suggestion.id}
          word={wordInput}
          {...suggestion}
          size={suggestions.length}
          position={index + 1}
          location={'llm'}
          isAdded={replacementInput === suggestion.label}
        />
      ))}
    </Box>
  );
};

const SmartSuggestionsBody = (props: SmartSuggestionsBodyProps) => {
  const { classes } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState<boolean | null>(null);

  useEffect(() => {
    if (containerRef && containerRef.current) {
      const element = containerRef.current;
      const canScrollElement = element.scrollHeight > element.clientHeight;
      setCanScroll(canScrollElement);
    }
  }, [canScroll]);

  return (
    <Box className={classes.bodyRoot}>{getBodyContents(props, classes)}</Box>
  );
};

export default SmartSuggestionsBody;
